import CheckoutContainer from '@components/CheckoutContainer'
import CustomPreloader from '@components/CustomPreloader'
import { PencilIcon } from '@icons'
import {
  CheckoutFlowStep,
  useBanksRequest,
  useCancelPaymentRequest,
  useCheckoutPayment,
  useEventTracker,
  useFilteredBanks,
  useLanguageParam,
  usePaymentOptions,
  usePaymentRequest,
  useRedirects,
  useSessionId,
  useUnloadBeacon
} from 'mmfintech-checkout-commons'
import type {
  CardPaymentAuthorizationDtlRequest,
  CheckoutMethodField,
  SupportedBank
} from 'mmfintech-checkout-commons/types'
import {
  formatFloat,
  GlobalContext,
  getCurrencyDecimals,
  isMultiItemArray,
  isSingleItemArray,
  isValidArray,
  isValidString,
  parseJson,
  tr
} from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import {
  type MouseEvent,
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import settings from '../../settings'
import { DepositResult } from '../DepositResult'
import { AdditionalFieldsStep } from './AdditionalFieldsStep'
import { BankTransferWarning1, BankTransferWarning2 } from './BankTransfer'
import styles from './Checkout.module.scss'
import { CreditCardAvs } from './CreditCardAvs'
import { CreditCardOtp } from './CreditCardOtp'
import { CreditCardPin } from './CreditCardPin'
import { SelectBankStep } from './SelectBankStep'
import { SelectMethodStep } from './SelectMethodStep'

import './checkout.scss'

export const Checkout = (): ReactElement => {
  const { modalShow } = useContext(GlobalContext)

  const {
    checkoutCountries,
    checkoutPay,
    checkoutPayError,
    checkoutPayFetching,
    session,
    walletPayError,
    walletPayFetching
  } = useSelector(
    ({
      checkout: {
        checkoutCountries,
        checkoutPay,
        checkoutPayError,
        checkoutPayFetching,
        session,
        walletPayError,
        walletPayFetching
      }
    }) => ({
      checkoutCountries,
      checkoutPay,
      checkoutPayError,
      checkoutPayFetching,
      session,
      walletPayError,
      walletPayFetching
    }),
    shallowEqual
  )

  const triggeredRef = useRef<boolean>(false)

  // const [triggered, setTriggered] = useState<boolean>(false)
  const [eventSubmitted, setEventSubmitted] = useState<boolean>(false)

  const sessionId = useSessionId()
  useLanguageParam(settings.languages)
  useUnloadBeacon(sessionId)

  const { cancelPayment } = useCancelPaymentRequest()
  const { reset: resetPayment, completeCreditCardPayment } = usePaymentRequest()

  const {
    step,
    setStep,
    fieldValues,
    countryCode,
    setCountryCode,
    paymentMethod,
    setPaymentMethod,
    selectedPaymentOption,
    handlePay,
    initialized,
    reloadSession,
    showFieldsAfterBanks
  } = useCheckoutPayment({
    initialStep: CheckoutFlowStep.SELECT_METHOD,
    preselectSingleBank: true,
    preselectSingleMethod: true
  })

  const { trackEvent } = useEventTracker()
  const { redirectToFailurePage } = useRedirects(sessionId)
  const { paymentOptions, paymentOptionsFetching } = usePaymentOptions()

  const { banksCount } = useBanksRequest()

  const isBankTransfer = useMemo(() => paymentMethod === 'BANKWIRE', [paymentMethod])

  const handleBankSelected = useCallback(
    (bank: SupportedBank): void => {
      const id = bank ? bank.bankChoiceId : ''
      fieldValues.setValue('bankChoiceId', id)
      if (id) {
        void trackEvent(sessionId, 'bank_selected', id)
      }
    },
    [sessionId, fieldValues, trackEvent]
  )

  const filteredBanks = useFilteredBanks(handleBankSelected)

  const hasMethodSelection = useMemo(
    () => isMultiItemArray(checkoutCountries) || isMultiItemArray(paymentOptions),
    [checkoutCountries, paymentOptions]
  )

  const handleCancel = useCallback(() => {
    if (sessionId) {
      cancelPayment(sessionId)
    }
    void trackEvent(sessionId, 'cancelled_by_payer')
    redirectToFailurePage()
    return false
  }, [sessionId, cancelPayment, trackEvent, redirectToFailurePage])

  const handleSubmit = useCallback(
    (onPay?: () => void) => {
      if (isBankTransfer) {
        if (fieldValues.areValid()) {
          setStep(CheckoutFlowStep.BANK_TRANSFER_WARNING1)
        }
        return
      }

      onPay?.()
      handlePay()
    },
    [fieldValues, isBankTransfer, handlePay, setStep]
  )

  const handleCountryChanged = useCallback(
    (value: string): void => {
      if (countryCode !== value) {
        filteredBanks.reset()
        setCountryCode(value)
      }
    },
    [countryCode, setCountryCode, filteredBanks]
  )

  const handleContinueAfterMethodSelection = useCallback(() => {
    if (fieldValues.hasAdditionalFields && !showFieldsAfterBanks) {
      setStep(CheckoutFlowStep.ADDITIONAL_FIELDS)
    } else if (fieldValues.hasBankSelection) {
      setStep(CheckoutFlowStep.SELECT_BANK)
    } else if (fieldValues.hasAdditionalFields && showFieldsAfterBanks) {
      setStep(CheckoutFlowStep.ADDITIONAL_FIELDS)
    } else {
      handleSubmit()
    }
  }, [fieldValues, showFieldsAfterBanks, handleSubmit, setStep])

  const handleBackFromAdditionalFields = useCallback(() => {
    resetPayment()
    if (showFieldsAfterBanks && fieldValues.hasBankSelection) {
      setStep(CheckoutFlowStep.SELECT_BANK)
    } else {
      setStep(CheckoutFlowStep.SELECT_METHOD)
    }
  }, [fieldValues, showFieldsAfterBanks, resetPayment, setStep])

  const handleContinueAfterAdditionalFields = useCallback(() => {
    if (fieldValues.areValid()) {
      if (fieldValues.hasBankSelection && !showFieldsAfterBanks) {
        setStep(CheckoutFlowStep.SELECT_BANK)
      } else {
        handleSubmit()
      }
    }
  }, [fieldValues, showFieldsAfterBanks, handleSubmit, setStep])

  const handleContinueAfterBankSelected = useCallback(() => {
    fieldValues.setRequired('bankChoiceId', true)
    if (showFieldsAfterBanks) {
      setStep(CheckoutFlowStep.ADDITIONAL_FIELDS)
    } else {
      handleSubmit()
    }
  }, [fieldValues, showFieldsAfterBanks, handleSubmit, setStep])

  const handleBackFromBankTransferWarning = useCallback(() => {
    if (fieldValues.hasAdditionalFields) {
      setStep(CheckoutFlowStep.ADDITIONAL_FIELDS)
      return
    }
    setStep(CheckoutFlowStep.SELECT_METHOD)
  }, [fieldValues, setStep])

  const handleBackFromBankSelection = useCallback(() => {
    resetPayment()
    if (!showFieldsAfterBanks && fieldValues.hasAdditionalFields) {
      setStep(CheckoutFlowStep.ADDITIONAL_FIELDS)
    } else {
      setStep(CheckoutFlowStep.SELECT_METHOD)
    }
  }, [fieldValues, showFieldsAfterBanks, resetPayment, setStep])

  const handleSearchFocus = useCallback(
    () => trackEvent(sessionId, 'bank_search_focus', null, true),
    [sessionId, trackEvent]
  )

  const handleSearchType = useCallback(() => {
    if (!eventSubmitted) {
      void trackEvent(sessionId, 'bank_search_type', null, true)
      setEventSubmitted(true)
    }
  }, [sessionId, trackEvent, eventSubmitted])

  const handleConfirmPayment = useCallback(
    (data: CardPaymentAuthorizationDtlRequest): void => {
      completeCreditCardPayment(sessionId, data)
    },
    [sessionId, completeCreditCardPayment]
  )

  const handleModalShow = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      modalShow(null)
    },
    [modalShow]
  )

  const prepareResponse = useCallback(() => {
    if (session?.sessionStatus?.value === 'IN_PROGRESS') {
      return {
        result: parseJson(session?.paymentResult, session?.paymentResult),
        resultType: session?.paymentResultType,
        processingAmount: session?.processingAmount,
        processingCurrency: session?.processingCurrency
      }
    }
    return checkoutPay
  }, [checkoutPay, session])

  const gotoBankTransferWarning1 = useCallback(() => setStep(CheckoutFlowStep.BANK_TRANSFER_WARNING1), [setStep])

  const gotoBankTransferWarning2 = useCallback(() => setStep(CheckoutFlowStep.BANK_TRANSFER_WARNING2), [setStep])

  const validMethodSelection = useMemo(
    () => isValidString(paymentMethod) && isSingleItemArray(paymentOptions),
    [paymentMethod, paymentOptions]
  )

  const validBankSelection = useMemo(
    () =>
      !fieldValues.hasBankSelection ||
      (fieldValues.hasBankSelection && banksCount === 1 && isValidString(fieldValues.getValue('bankChoiceId'))),
    [fieldValues, banksCount]
  )

  const validFields = useMemo(() => {
    if (isValidArray(selectedPaymentOption?.fields)) {
      if (selectedPaymentOption.fields.length !== fieldValues.count()) return false

      return !selectedPaymentOption.fields.some(
        (field: CheckoutMethodField) =>
          (!field.prefilledValue || field.displayIfPrefilled) && field.name !== 'bankChoiceId'
      )
    }
    return true
  }, [selectedPaymentOption, fieldValues])

  // biome-ignore lint/correctness/useExhaustiveDependencies: only trigger if methods, banks and fields are valid
  useEffect(() => {
    if (
      initialized &&
      !triggeredRef.current &&
      !walletPayFetching &&
      validMethodSelection &&
      validBankSelection &&
      validFields
    ) {
      triggeredRef.current = true
      handleSubmit()
    }
  }, [initialized, validMethodSelection, validBankSelection, validFields])

  // biome-ignore lint/correctness/useExhaustiveDependencies: handle the walletPayError
  useEffect(() => {
    if (walletPayError) {
      if (walletPayError.errorKey === 'MERCHANTS.ERROR.PAYMENT_ALREADY_COMPLETED') {
        reloadSession()
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    }
  }, [walletPayError])

  return (
    <CheckoutContainer>
      {!initialized ? (
        <div className='payment-container'>
          <CustomPreloader />
        </div>
      ) : (
        <>
          {Boolean(session?.amount) && (
            <div className='amount-container'>
              <div className='row amount'>
                <span data-test='amount'>{formatFloat(session?.amount, getCurrencyDecimals(session?.currency))}</span>
                <span className='currency' data-test='currency'>
                  {session?.currency}
                </span>
              </div>
              {Boolean(session?.foreignTransactionId) && (
                <div className='row'>
                  <span className='label'>{tr('CHECKOUT.PAYMENT.ID', 'ID')}:</span>
                  <span data-test='transaction-id'>{session?.foreignTransactionId}</span>
                </div>
              )}
            </div>
          )}

          <div className='payment-container'>
            {step !== CheckoutFlowStep.SELECT_BANK &&
            step !== CheckoutFlowStep.SELECT_METHOD &&
            step !== CheckoutFlowStep.ADDITIONAL_FIELDS &&
            step !== CheckoutFlowStep.CREDIT_CARD_AVS &&
            step !== CheckoutFlowStep.CREDIT_CARD_OTP &&
            step !== CheckoutFlowStep.CREDIT_CARD_PIN ? (
              <ErrorDisplay error={walletPayError} />
            ) : null}

            {step === CheckoutFlowStep.SELECT_METHOD && !triggeredRef.current && (
              <SelectMethodStep
                countryCode={countryCode}
                setCountryCode={handleCountryChanged}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                paymentOptions={paymentOptions}
                paymentOptionsFetching={paymentOptionsFetching}
                onContinue={handleContinueAfterMethodSelection}
                showCancel={session?.showCancelButton}
                onCancel={handleCancel}
              />
            )}

            {step === CheckoutFlowStep.ADDITIONAL_FIELDS ? (
              <AdditionalFieldsStep
                error={checkoutPayError ?? walletPayError}
                backVisible={hasMethodSelection || (showFieldsAfterBanks && fieldValues.hasBankSelection)}
                onBack={handleBackFromAdditionalFields}
                countryCode={countryCode}
                paymentMethod={paymentMethod}
                fieldValues={fieldValues}
                disabled={checkoutPayFetching}
                submitCaption={
                  (fieldValues.hasBankSelection && !showFieldsAfterBanks) || isBankTransfer
                    ? tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')
                    : tr('CHECKOUT.PAYMENT.BUTTON_PAY', 'Pay')
                }
                onCancel={handleCancel}
                onSubmit={handleContinueAfterAdditionalFields}
                cancelVisible={session?.showCancelButton}
              />
            ) : null}

            {step === CheckoutFlowStep.SELECT_BANK ? (
              <SelectBankStep
                backCaption={
                  fieldValues.hasAdditionalFields && !showFieldsAfterBanks
                    ? tr('FRONTEND.BUTTONS.BACK', 'Back')
                    : tr('CHECKOUT.PAYMENT.SWITCH_METHOD', 'Switch method')
                }
                backVisible={hasMethodSelection || (!showFieldsAfterBanks && fieldValues.hasAdditionalFields)}
                onBack={handleBackFromBankSelection}
                paymentMethod={paymentMethod}
                countryCode={countryCode}
                fieldValues={fieldValues}
                filteredBanks={filteredBanks}
                onContinue={handleContinueAfterBankSelected}
                cancelVisible={session?.showCancelButton}
                onCancel={handleCancel}
                handleSearchFocus={handleSearchFocus}
                handleSearchType={handleSearchType}
              />
            ) : null}

            {step === CheckoutFlowStep.CREDIT_CARD_AVS && (
              <CreditCardAvs
                onSubmit={handleConfirmPayment}
                onCancel={session?.showCancelButton ? handleCancel : null}
              />
            )}
            {step === CheckoutFlowStep.CREDIT_CARD_OTP && (
              <CreditCardOtp
                onSubmit={handleConfirmPayment}
                onCancel={session?.showCancelButton ? handleCancel : null}
              />
            )}
            {step === CheckoutFlowStep.CREDIT_CARD_PIN && (
              <CreditCardPin
                onSubmit={handleConfirmPayment}
                onCancel={session?.showCancelButton ? handleCancel : null}
              />
            )}

            {step === CheckoutFlowStep.BANK_TRANSFER_WARNING1 && (
              <BankTransferWarning1
                handleNext={gotoBankTransferWarning2}
                handleBack={triggeredRef.current ? undefined : handleBackFromBankTransferWarning}
                paymentMethod={paymentMethod}
                onCancel={session?.showCancelButton ? handleCancel : null}
              />
            )}
            {step === CheckoutFlowStep.BANK_TRANSFER_WARNING2 && (
              <BankTransferWarning2
                handleNext={handlePay}
                handleBack={gotoBankTransferWarning1}
                paymentMethod={paymentMethod}
                onCancel={session?.showCancelButton ? handleCancel : null}
              />
            )}

            {/* TODO: step === CheckoutFlowStep.INTERNAL_EXECUTION <InternalPayment session={checkoutPay} /> */}

            {step === CheckoutFlowStep.DEPOSIT_RESULT && (
              <DepositResult session={session} sessionId={sessionId} response={prepareResponse()} />
            )}
          </div>

          {Boolean(sessionId) && (
            <div className={styles['checkout-footer']}>
              <div className={styles['footer-link']} onClick={handleModalShow} data-test='report-a-problem-button'>
                <PencilIcon />
                <span>{tr('CHECKOUT.PROBLEM.TITLE', 'Report a Problem')}</span>
              </div>

              {Boolean(session?.returnUrl) && (
                <a className={styles['footer-link']} href={session?.returnUrl} target='_top'>
                  &larr; {tr('CHECKOUT.PAYMENT.BACK_TO_MERCHANT', 'Back to merchant')}
                </a>
              )}
            </div>
          )}
        </>
      )}
    </CheckoutContainer>
  )
}
